/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, mutatingPost, type ApiResult } from "./client";
import { withSort, type SortChoice } from "../lib/sorting";

/** Mirror of nexus DuplicateRoutes.AccountSide. */
export type AccountSide = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  username: string | null;
  phone: string | null;
  status: string;
  familyId: number | null;
  ehrUserId: string | null;
  createdAt: string | null;
};

export type DuplicateSignal =
  | "CHILD_NAME_DOB"
  | "ADULT_PHONE_FULL_NAME"
  | "ADULT_PHONE_LAST_NAME"
  | "ADULT_PHONE";

/** Mirror of nexus DuplicateRoutes.CaseView. */
export type DuplicateCase = {
  id: string;
  accountA: AccountSide;
  accountB: AccountSide;
  signals: DuplicateSignal[];
  score: number;
  status: "OPEN" | "DISMISSED" | "MERGED";
  survivorUserId: number | null;
  resolutionNote: string | null;
  createdAt: string | null;
};

export type SignalInfo = {
  label: string;
  weight: number;
  description: string;
};

/**
 * Mirror of nexus DuplicateSignal weights. A signal is a recorded fact that two accounts look
 * alike; the descriptions state exactly what matched, because "same child" means "two child
 * accounts whose recorded identity matches", never a verified human. Names "agree" when they are
 * the same or nearly so (a typo or a spelling variant), never on a different name.
 */
export const SIGNALS: Record<DuplicateSignal, SignalInfo> = {
  CHILD_NAME_DOB: {
    label: "Matching child name and birth date",
    weight: 3,
    description:
      "Two accounts with the Child role, in different families, whose first and last names agree" +
      " and whose dates of birth are identical.",
  },
  ADULT_PHONE_FULL_NAME: {
    label: "Matching adult phone number and full name",
    weight: 3,
    description:
      "Two adult client accounts (parents or standalone clients), in different families," +
      " sharing the same phone number, whose first and last names agree.",
  },
  ADULT_PHONE_LAST_NAME: {
    label: "Matching adult phone number and last name",
    weight: 2,
    description:
      "Two adult client accounts, in different families, sharing the same phone number, whose" +
      " last names agree but whose first names differ.",
  },
  ADULT_PHONE: {
    label: "Matching adult phone number (no longer detected)",
    weight: 1,
    description:
      "Two adult client accounts sharing a phone number and nothing else. Earlier scans opened" +
      " cases on this alone; the sweep no longer does, since households share a line by design.",
  },
};

/** The range a case the sweep opens today can score: its signals never co-occur, so one weight. */
export const SCORE_MIN = SIGNALS.ADULT_PHONE_LAST_NAME.weight;
export const SCORE_MAX = SIGNALS.ADULT_PHONE_FULL_NAME.weight;

export function listOpenCases(
  limit: number,
  offset: number,
  q = "",
  sort?: SortChoice,
): Promise<ApiResult<{ items: DuplicateCase[]; total: number }>> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (q.trim() !== "") {
    params.set("q", q.trim());
  }
  if (sort !== undefined) {
    withSort(params, sort);
  }
  return api<{ items: DuplicateCase[]; total: number }>(`/v1/duplicates/?${params.toString()}`);
}

export function getCase(id: string): Promise<ApiResult<DuplicateCase>> {
  return api<DuplicateCase>(`/v1/duplicates/${id}`);
}

export function confirmMerge(
  id: string,
  survivorUserId: number,
  note: string,
): Promise<ApiResult<DuplicateCase>> {
  return api<DuplicateCase>(`/v1/duplicates/${id}/confirm`, mutatingPost({ survivorUserId, note }));
}

export function dismissCase(id: string, note: string): Promise<ApiResult<DuplicateCase>> {
  return api<DuplicateCase>(`/v1/duplicates/${id}/dismiss`, mutatingPost({ note }));
}

/** Queues a sweep for cron to run; new cases land in the queue once it finishes. */
export function triggerScan(): Promise<ApiResult<{ status: string }>> {
  return api<{ status: string }>("/v1/duplicates/scan", mutatingPost({}));
}
